import type { MandateProps } from '@gitbook/util-ts';

import type { AnimationPhases } from './animation';

const TIME_SLOWDOWN = process.env.GITBOOK_PREDICTABLE_UI ? 0 : 1;

export const TRANSITION_DURATION_XSHORT = TIME_SLOWDOWN * 100;
export const TRANSITION_DURATION_SHORT = TIME_SLOWDOWN * 200;
export const TRANSITION_DURATION_MEDIUM = TIME_SLOWDOWN * 420;
export const TRANSITION_DURATION_LONG = TIME_SLOWDOWN * 800;

/**
 * Animation to progressively show and hide an element using its opacity
 */
export const fade: MandateProps<AnimationPhases, 'mount' | 'unmount'> = {
    mount: {
        duration: TRANSITION_DURATION_XSHORT,
        frames: {
            0: {
                opacity: 0,
            },
            1: {
                opacity: 1,
            },
        },
    },
    unmount: {
        duration: TRANSITION_DURATION_XSHORT,
        frames: {
            0: {
                opacity: 1,
            },
            1: {
                opacity: 0,
            },
        },
    },
};

/**
 * Animation to pop-up an element on the screen (popover, etc).
 * Use transformOrigin property to specify origin point.
 */
export const pop: MandateProps<AnimationPhases, 'mount' | 'unmount'> = {
    mount: {
        duration: TRANSITION_DURATION_SHORT,
        frames: {
            0: {
                opacity: 0,
                transform: [
                    {
                        scale: 0.9,
                    },
                ],
            },
            1: {
                opacity: 1,
                transform: [
                    {
                        scale: 1,
                    },
                ],
            },
        },
    },
    unmount: {
        duration: TRANSITION_DURATION_SHORT,
        frames: {
            0: {
                opacity: 1,
                transform: [
                    {
                        scale: 1,
                    },
                ],
            },
            1: {
                opacity: 0,
                transform: [
                    {
                        scale: 0.9,
                    },
                ],
            },
        },
    },
};

/**
 * Animation to slide in an element on the screen (menus, etc).
 */
export const slideToL = {
    left: createSlideToAnimation('X', '10%', TRANSITION_DURATION_SHORT, TRANSITION_DURATION_XSHORT),
    right: createSlideToAnimation(
        'X',
        '-10%',
        TRANSITION_DURATION_SHORT,
        TRANSITION_DURATION_XSHORT
    ),
    top: createSlideToAnimation('Y', '10%', TRANSITION_DURATION_SHORT, TRANSITION_DURATION_XSHORT),
    bottom: createSlideToAnimation(
        'Y',
        '-10%',
        TRANSITION_DURATION_SHORT,
        TRANSITION_DURATION_XSHORT
    ),
};
/**
 * Animation to slide in an element on the screen (menus, etc).
 * Small variant of animation, used for tooltips.
 */
export const slideToS = {
    left: createSlideToAnimation('X', 5, TRANSITION_DURATION_SHORT, TRANSITION_DURATION_XSHORT),
    right: createSlideToAnimation('X', -5, TRANSITION_DURATION_SHORT, TRANSITION_DURATION_XSHORT),
    top: createSlideToAnimation('Y', 5, TRANSITION_DURATION_SHORT, TRANSITION_DURATION_XSHORT),
    bottom: createSlideToAnimation('Y', -5, TRANSITION_DURATION_SHORT, TRANSITION_DURATION_XSHORT),
};

/**
 * Create a slide animation for a given direction.
 */
function createSlideToAnimation(
    axis: 'X' | 'Y',
    move: number | string,
    durationIn: number,
    durationOut: number
): AnimationPhases {
    const property = axis === 'X' ? 'translateX' : 'translateY';

    return {
        mount: {
            duration: durationIn,
            frames: {
                0: {
                    opacity: 0,
                    transform: [
                        // @ts-ignore
                        {
                            [property]: move,
                        },
                    ],
                },
                1: {
                    opacity: 1,
                    transform: [
                        // @ts-ignore
                        {
                            [property]: 0,
                        },
                    ],
                },
            },
        },
        unmount: {
            duration: durationOut,
            frames: {
                0: {
                    opacity: 1,
                    transform: [
                        // @ts-ignore
                        {
                            [property]: 0,
                        },
                    ],
                },
                1: {
                    opacity: 0,
                    transform: [
                        // @ts-ignore
                        {
                            [property]: move,
                        },
                    ],
                },
            },
        },
    };
}
